/**
 * Root Application Component
 *
 * Sets up routing, theme provider, and the main layout shell.
 * AppShell (sidebar + header) is mounted once as a layout route,
 * so it persists across page navigations without re-mounting.
 */

import { Component, lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { LoadingShimmer } from './components/ui';
import { useAuth } from './hooks/useAuth';
import { capabilityForRoute, firstAllowedRoute } from './lib/capabilityRoutes';
import { ThemeProvider } from './contexts/ThemeContext';
import { TourProvider } from './components/tour/TourContext';

class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: '#dc2626', marginBottom: 8 }}>Page crashed</h2>
          <pre style={{ background: '#fef2f2', padding: 16, borderRadius: 6, whiteSpace: 'pre-wrap', color: '#7f1d1d', fontSize: 13 }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
import { UpdateBanner } from './components/UpdateBanner';
import { AppShell } from './components/layout/AppShell';

/**
 * Every page loads on demand (its own chunk), so a visit parses only the shell
 * plus the page it opens instead of the whole app. That matters most in the
 * Compass App, where each screen is a fresh WebView. The fallback renders
 * inside AppShell (layout/AppShell.tsx), so the Sidebar and Header stay put.
 */
function lazyNamed<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(async () => ({ default: (await load())[name] as unknown as ComponentType<{ children?: ReactNode }> }));
}

const Dashboard = lazyNamed(() => import('./pages/Dashboard'), 'Dashboard');
const DataExplorer = lazyNamed(() => import('./pages/DataExplorer'), 'DataExplorer');
const AccountsList = lazyNamed(() => import('./pages/accounts/AccountsList'), 'AccountsList');
const AccountDetail = lazyNamed(() => import('./pages/accounts/AccountDetail'), 'AccountDetail');
const SnowflakeAccountsList = lazyNamed(() => import('./pages/accounts/SnowflakeAccountsList'), 'SnowflakeAccountsList');
const SnowflakeAccountDetail = lazyNamed(() => import('./pages/accounts/SnowflakeAccountDetail'), 'SnowflakeAccountDetail');
const ParentAccountsList = lazyNamed(() => import('./pages/accounts/ParentAccountsList'), 'ParentAccountsList');
const ParentAccountDetail = lazyNamed(() => import('./pages/accounts/ParentAccountDetail'), 'ParentAccountDetail');
const ScoutPage = lazy(() => import('./pages/scout/ScoutPage'));
const MeetingsPage = lazyNamed(() => import('./pages/meetings/MeetingsPage'), 'MeetingsPage');
const Connectors = lazyNamed(() => import('./pages/Connectors'), 'Connectors');
const ConnectorDetail = lazyNamed(() => import('./pages/ConnectorDetail'), 'ConnectorDetail');
const SyncMonitor = lazyNamed(() => import('./pages/SyncMonitor'), 'SyncMonitor');
const AdminWbrRuns = lazy(() => import('./pages/AdminWbrRuns'));
const SettingsPage = lazyNamed(() => import('./pages/Settings'), 'SettingsPage');
const FeatureFlags = lazyNamed(() => import('./pages/FeatureFlags'), 'FeatureFlags');
const RbacRoles = lazyNamed(() => import('./pages/RbacRoles'), 'RbacRoles');
const RbacCategories = lazyNamed(() => import('./pages/RbacCategories'), 'RbacCategories');
const RbacFieldCategories = lazyNamed(() => import('./pages/RbacFieldCategories'), 'RbacFieldCategories');
const RbacRoleMatrix = lazyNamed(() => import('./pages/RbacRoleMatrix'), 'RbacRoleMatrix');
const RbacUserRoles = lazyNamed(() => import('./pages/RbacUserRoles'), 'RbacUserRoles');
const FileCategoriesPanel = lazyNamed(() => import('./pages/FileCategoriesPanel'), 'FileCategoriesPanel');
const HardcodedUsersPanel = lazyNamed(() => import('./pages/HardcodedUsersPanel'), 'HardcodedUsersPanel');
const RbacSimulator = lazyNamed(() => import('./pages/RbacSimulator'), 'RbacSimulator');
const AuditLog = lazyNamed(() => import('./pages/AuditLog'), 'AuditLog');
const ReferenceData = lazyNamed(() => import('./pages/ReferenceData'), 'ReferenceData');
const SystemHealth = lazyNamed(() => import('./pages/SystemHealth'), 'SystemHealth');
const CacheManagement = lazyNamed(() => import('./pages/CacheManagement'), 'CacheManagement');
const AppLog = lazyNamed(() => import('./pages/AppLog'), 'AppLog');
const UserSettings = lazyNamed(() => import('./pages/UserSettings'), 'UserSettings');
const ZoomBrowser = lazyNamed(() => import('./pages/connectors/ZoomBrowser'), 'ZoomBrowser');
const PersonalSpacePage = lazyNamed(() => import('./pages/personal/PersonalSpacePage'), 'PersonalSpacePage');
const GoogleBrowser = lazyNamed(() => import('./pages/connectors/GoogleBrowser'), 'GoogleBrowser');
const MicrosoftBrowser = lazyNamed(() => import('./pages/connectors/MicrosoftBrowser'), 'MicrosoftBrowser');
const Login = lazyNamed(() => import('./pages/Login'), 'Login');
const AdminDataExplorer = lazyNamed(() => import('./pages/AdminDataExplorer'), 'AdminDataExplorer');
const EmbedBakeoff = lazyNamed(() => import('./pages/EmbedBakeoff'), 'EmbedBakeoff');
const OperationsExplorer = lazyNamed(() => import('./pages/OperationsExplorer'), 'OperationsExplorer');
const ClinicalExplorer = lazyNamed(() => import('./pages/ClinicalExplorer'), 'ClinicalExplorer');
const ClinicalSuccessPage = lazyNamed(() => import('./pages/ClinicalSuccessPage'), 'ClinicalSuccessPage');
const FinancePage = lazyNamed(() => import('./pages/finance/FinancePage'), 'FinancePage');
const ClinicalSuccessBoard = lazyNamed(() => import('./pages/ClinicalSuccessBoard'), 'ClinicalSuccessBoard');
const ClinicalTerritoryAnalysis = lazyNamed(() => import('./pages/ClinicalTerritoryAnalysis'), 'ClinicalTerritoryAnalysis');
const ProductExplorer = lazyNamed(() => import('./pages/ProductExplorer'), 'ProductExplorer');
const DashboardsGallery = lazyNamed(() => import('./pages/dashboards/Gallery'), 'DashboardsGallery');
const DashboardsCreate = lazyNamed(() => import('./pages/dashboards/CreateDashboard'), 'DashboardsCreate');
const DashboardsFileBrowser = lazyNamed(() => import('./pages/dashboards/FileBrowser'), 'DashboardsFileBrowser');
const DashboardsViewer = lazyNamed(() => import('./pages/dashboards/Viewer'), 'DashboardsViewer');
const Briefings = lazyNamed(() => import('./pages/Briefings'), 'Briefings');
const BriefingChatProvider = lazyNamed(() => import('./pages/briefings/ChatDrawer'), 'BriefingChatProvider');
const MySalesforce = lazyNamed(() => import('./pages/my-salesforce/MySalesforce'), 'MySalesforce');
const RepHygieneDetail = lazyNamed(() => import('./pages/my-salesforce/RepHygieneDetail'), 'RepHygieneDetail');
const CompassAiPage = lazyNamed(() => import('./pages/CompassAiPage'), 'CompassAiPage');
const Help = lazyNamed(() => import('./pages/Help'), 'Help');
const NeedsYouPage = lazyNamed(() => import('./pages/approvals/NeedsYouPage'), 'NeedsYouPage');
// CO-276: RbacSsoMappings deleted (Okta-group → role pipeline retired).
// RbacUserOverrides renamed to RbacUserRoles.

/** Shown while a page outside AppShell (Login) loads its chunk. */
function PageLoading() {
  return <LoadingShimmer rows={4} label="Loading page" data-testid="page-loading" />;
}

function ProtectedLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg-primary)' }}
      >
        <div style={{ width: 320 }}>
          <LoadingShimmer rows={3} label="Signing you in" data-testid="auth-loading" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // AppShell mounts once here — Sidebar and Header persist across all child routes
  return (
    <>
      <UpdateBanner />
      <AppShell />
    </>
  );
}

/**
 * Client-side gate for /admin/* routes. Server enforces requireAdmin
 * on /api/admin/*, but without this gate non-admin users who type the
 * URL would see broken admin shells with no data. Redirect them home.
 */
function AdminRoute() {
  const { user, loading } = useAuth();
  // Not `null`: that flashed a blank page on every hard load of /admin/*.
  if (loading) return <LoadingShimmer rows={4} label="Loading admin" data-testid="admin-route-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Client-side gate for /compass-ai. The server enforces `requireCompassAi` on
 * every chat route, so a seatless user who types the URL would reach a chat
 * page whose first message 403s — send them home instead. Mirrors the hidden
 * sidebar entry and the suppressed Ask CompassAI pills.
 */
function CompassAiRoute() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingShimmer rows={4} label="Loading CompassAI" data-testid="compass-ai-route-loading" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.hasCompassAi) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * Client-side mirror of the capability gates: if the current path belongs to a
 * capability-gated surface the user does not hold, send them home. The server
 * `requireCapability` gates are the real boundary — this only stops a user who
 * types a URL from landing on a page whose API calls would 403. Admins get the
 * full catalog from /auth/me so this is a no-op for them. The route → capability
 * map lives in `lib/capabilityRoutes.ts` (mirrors src/auth/rbac/capabilities.ts).
 */
function VisibilityGuard() {
  const { user } = useAuth();
  const location = useLocation();
  // Until /auth/me answers there is nothing to compare against; ProtectedLayout
  // already shows the loading state, so render through.
  if (!user) return <Outlet />;
  if (user.isAdmin) return <Outlet />;
  const held = new Set(user.capabilities ?? []);
  const needed = capabilityForRoute(location.pathname);
  if (needed && !held.has(needed)) {
    const fallback = firstAllowedRoute(held);
    if (location.pathname === fallback) return <Outlet />;
    return <Navigate to={fallback} replace />;
  }
  return <Outlet />;
}

function App() {
  return (
    <ThemeProvider>
      <TourProvider>
      <Router future={{ v7_startTransition: true }}>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* All protected routes share a single AppShell instance */}
          <Route element={<ProtectedLayout />}>
            <Route element={<VisibilityGuard />}>
            <Route path="/" element={<Dashboard />} />
            {/* The Compass App's spare WebView idles here, booted and signed in, until a
                section takes it (EMBEDDED_WARM_PATH in src/contracts/app-catalog.ts). */}
            <Route path="/embedded/warm" element={null} />
            {/* Dedicated CompassAI chat page (its own route, not a dock).
                Behind CompassAiRoute — needs a CompassAI seat. */}
            <Route element={<CompassAiRoute />}>
              <Route path="/compass-ai" element={<CompassAiPage />} />
              {/* Old address from the 2026-10-09 → 2026-10-11 interlude when the assistant was called Wingman. */}
              <Route path="/wingman" element={<Navigate to="/compass-ai" replace />} />
            </Route>
            {/* Legacy explorer URLs → unified Data Explorer with correct tab */}
            <Route path="/admin/snowflake-explorer" element={<Navigate to="/admin/data-explorer?tab=snowflake" replace />} />
            <Route path="/admin/clickhouse-explorer" element={<Navigate to="/admin/data-explorer?tab=clickhouse" replace />} />
            <Route path="/admin/salesforce-explorer" element={<Navigate to="/admin/data-explorer?tab=salesforce" replace />} />
            <Route path="/admin/s3-explorer" element={<Navigate to="/admin/data-explorer?tab=s3" replace />} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/personal" element={<PersonalSpacePage />} />
            {/* Connectors (docs/Integrations/Connectors): browse what a connected
                account lets Compass see. Same gate as My Settings on the server. */}
            <Route path="/connectors/zoom" element={<ZoomBrowser />} />
            <Route path="/connectors/google" element={<GoogleBrowser />} />
            <Route path="/connectors/microsoft" element={<MicrosoftBrowser />} />
            <Route path="/help" element={<Help />} />
            {/* "Needs you" — the approval queue (src/approvals, Phase 2 APR-01/02/05). Per-person and
                owner-only on the server, so not capability-gated (like /help); reached from the Header. */}
            <Route path="/approvals" element={<NeedsYouPage />} />
            <Route path="/approvals/:id" element={<NeedsYouPage />} />
            <Route path="/data-explorer" element={<DataExplorer />} />
            {/* Accounts (CO-437) — list + detail. */}
            <Route path="/accounts" element={<AccountsList />} />
            {/* Parent accounts tab — declared BEFORE /accounts/:sfId so the
                literal "parents" segment isn't captured as a Salesforce id. */}
            <Route path="/accounts/parents" element={<ParentAccountsList />} />
            <Route path="/accounts/parents/:sfId" element={<ParentAccountDetail />} />
            {/* Snowflake source tab — declared BEFORE /accounts/:sfId so the
                literal "snowflake" segment isn't captured as a Salesforce id.
                Server gates these endpoints to unrestricted viewers (403). */}
            <Route path="/accounts/snowflake" element={<SnowflakeAccountsList />} />
            <Route path="/accounts/snowflake/:accountKey" element={<SnowflakeAccountDetail />} />
            <Route path="/accounts/:sfId" element={<AccountDetail />} />
            <Route path="/scout" element={<ScoutPage />} />
            {/* Meetings (docs/Features/Meetings): one page — the feed (shell B) with its
                History, Review mode, To-dos and Venues views; the URL carries view + meeting + panel (MT-144). */}
            <Route path="/meetings" element={<MeetingsPage />} />
            <Route path="/meetings/history" element={<MeetingsPage />} />
            <Route path="/meetings/history/:id" element={<MeetingsPage />} />
            <Route path="/meetings/history/:id/:panel" element={<MeetingsPage />} />
            <Route path="/meetings/review" element={<MeetingsPage />} />
            <Route path="/meetings/review/:id" element={<MeetingsPage />} />
            <Route path="/meetings/review/:id/:panel" element={<MeetingsPage />} />
            <Route path="/meetings/calendar" element={<MeetingsPage />} />
            <Route path="/meetings/todos" element={<MeetingsPage />} />
            <Route path="/meetings/venues" element={<MeetingsPage />} />
            <Route path="/meetings/venues/:venueId" element={<MeetingsPage />} />
            <Route path="/meetings/:id" element={<MeetingsPage />} />
            <Route path="/meetings/:id/:panel" element={<MeetingsPage />} />
            <Route path="/operations" element={<OperationsExplorer />} />
            <Route path="/clinical" element={<ClinicalExplorer />} />
            <Route path="/clinical-success" element={<ClinicalSuccessPage />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/clinical/success-board" element={<ClinicalSuccessBoard />} />
            <Route path="/clinical/territory-analysis" element={<ClinicalTerritoryAnalysis />} />
            <Route path="/product" element={<ProductExplorer />} />
            {/* All /admin/* routes go inside this block — AdminRoute
                gates the entire subtree, so any future admin page is
                automatically admin-only. Do NOT add /admin/* routes
                outside this parent. */}
            <Route path="/admin/*" element={<AdminRoute />}>
              <Route path="data-explorer" element={<AdminDataExplorer />} />
              <Route path="connectors" element={<Connectors />} />
              <Route path="connectors/:id" element={<ConnectorDetail />} />
              <Route path="sync-monitor" element={<SyncMonitor />} />
              <Route path="wbr-runs" element={<AdminWbrRuns />} />
              <Route path="settings" element={<RouteErrorBoundary><SettingsPage /></RouteErrorBoundary>} />
              <Route path="feature-flags" element={<FeatureFlags />} />
              <Route path="rbac/roles" element={<RbacRoles />} />
              <Route path="rbac/categories" element={<RbacCategories />} />
              <Route path="rbac/field-categories" element={<RbacFieldCategories />} />
              <Route path="rbac/role-matrix" element={<RbacRoleMatrix />} />
              <Route path="rbac/user-roles" element={<RbacUserRoles />} />
              {/* CO-276: legacy /admin/rbac/user-overrides keeps working
                  as a route alias so any bookmarked admin tabs stop on the
                  renamed page rather than 404. */}
              <Route path="rbac/user-overrides" element={<RbacUserRoles />} />
              <Route path="rbac/file-categories" element={<FileCategoriesPanel />} />
              <Route path="rbac/hardcoded-users" element={<HardcodedUsersPanel />} />
              <Route path="rbac/simulator" element={<RbacSimulator />} />
              <Route path="reference-data" element={<ReferenceData />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="app-log" element={<AppLog />} />
              {/* Backwards-compat: the old per-source paths redirect to the unified page. */}
              <Route path="prod-log" element={<AppLog />} />
              <Route path="local-log" element={<AppLog />} />
              <Route path="cache" element={<CacheManagement />} />
              {/* S3 File Browser is the same <S3Explorer/> as Data Explorer →
                  S3 Storage; redirect the standalone path to the canonical tab. */}
              <Route path="file-browser" element={<Navigate to="/admin/data-explorer?tab=s3" replace />} />
              <Route path="health" element={<SystemHealth />} />
              <Route path="embed-bakeoff" element={<EmbedBakeoff />} />
              {/* Unknown /admin/* path: send admins home rather than
                  bubbling to the global catch-all (which would do the
                  same, but this keeps the intent explicit). */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>

            {/* Create-Dashboards (CO-74). Order: more-specific paths first
                so /dashboards/create and /dashboards/files don't collide
                with the :id viewer route. */}
            <Route path="/dashboards" element={<DashboardsGallery />} />
            <Route path="/dashboards/create" element={<DashboardsCreate />} />
            <Route path="/dashboards/files" element={<DashboardsFileBrowser />} />
            <Route path="/dashboards/:id" element={<DashboardsViewer />} />

            {/* Weekly Briefing Report — two-pane explorer (post 2026-05-14
                UX iteration). Single page; left pane has RBAC-scoped
                person dropdown + week list, right pane renders the
                selected (person, week) briefing. The wildcard `/briefings/*`
                catches legacy URLs (`/briefings/ae`, `/briefings/node/:id`)
                and lands them on the same page — selection happens via
                `?person=` / `?week=` query params, not path. */}
            <Route
              path="/briefings/*"
              element={
                <BriefingChatProvider>
                  <Briefings />
                </BriefingChatProvider>
              }
            />
            {/* My Salesforce — AE daily hygiene cockpit (Salesforce Hygiene
                Phase 1). Self-scoped, read-only; the page handles the
                feature-disabled / unscored states on its own. */}
            <Route path="/my-salesforce" element={<MySalesforce />} />
            <Route path="/my-salesforce/rep/:ownerSfId" element={<RepHygieneDetail />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
      </TourProvider>
    </ThemeProvider>
  );
}

export default App;
